import { Button, Divider, LinearProgress, Stack } from "@mui/material";
import React, { FC, useContext, useState } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { FormattedMessage, useIntl } from "react-intl";
import { theme } from "../../../styles/themeProvider";
import { UserApi } from "../../../api/userApi";
import {
	BusinessDataContext,
	LoginContext,
	LoginContextValues,
	MultistepContext,
	MultistepContextValues,
} from "../../../types/context";
import GoogleLogin from "./GoogleLogin";
import { ErrorTypography, FormField } from "../../styled/formComponents";
import { findComponentStep } from "../../../utils/filter";
import { ForgotPasswordButton } from "../../buttons/ForgotPasswordButton";
import { RegisterButton } from "../../buttons/RegisterButton";
import { tryCatchHandler } from "../../../utils/tryCatchHandler";

interface LoginValues {
	email: string;
	password: string;
}

const Login: FC<{ setGoogleError: Function }> = ({ setGoogleError }) => {
	const { onChangeLogin, onNext, onChangeStep } = useContext(
		MultistepContext
	) as MultistepContextValues;

	const businessDataContext = useContext(BusinessDataContext);

	const { onLogin, isCalledFromProfile } = useContext(
		LoginContext
	) as LoginContextValues;

	const { formatMessage } = useIntl();
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);

	const initialValues = {
		email: "",
		password: "",
	};

	const requiredMessage = formatMessage({
		id: "error.required",
		defaultMessage: "Campo requerido.",
	});

	const loginSchema = Yup.object().shape({
		email: Yup.string()
			.trim()
			.required(requiredMessage)
			.email(
				formatMessage({
					id: "error.email",
					defaultMessage: "Debe ingresar un mail.",
				})
			),
		password: Yup.string().required(requiredMessage),
	});

	const handleLogin = async (values: LoginValues) => {
		setError("");
		setLoading(true);
		const userApi = new UserApi();

		const errorMessage = await tryCatchHandler(
			async () => {
				const response = await userApi.login(values.email, values.password);
				onLogin();
				onChangeLogin(response.data);
				if (isCalledFromProfile) {
					const nextStep = findComponentStep(
						businessDataContext!.selectedFlow!,
						"Profile"
					);
					onChangeStep(nextStep);
				} else {
					onNext();
				}
			},
			"emailLogin.invalidData",
			"Email o contraseña incorrectos.",
			formatMessage
		);

		if (errorMessage) setError(errorMessage);

		setLoading(false);
	};

	return (
		<Stack sx={{ width: { mobile: "85%", xs: "75%", sm: "70%", md: "65%" } }}>
			<Formik
				initialValues={initialValues}
				validationSchema={loginSchema}
				onSubmit={async (values) => await handleLogin(values)}
			>
				{({
					values,
					errors,
					touched,
					handleChange,
					handleBlur,
					handleSubmit,
					isSubmitting,
				}) => (
					<Form style={{ width: "100%", alignItems: "center" }}>
						<Stack spacing={2}>
							<FormField
								name="email"
								label="Email"
								type="email"
								value={values.email}
								touched={touched.email}
								error={errors.email}
								handleBlur={handleBlur}
								handleChange={handleChange}
							/>
							<FormField
								name="password"
								label="Contraseña"
								type="password"
								autoComplete="current-password"
								value={values.password}
								touched={touched.password}
								error={errors.password}
								handleBlur={handleBlur}
								handleChange={handleChange}
							/>

							{error && <ErrorTypography>{error}</ErrorTypography>}
							{loading && <LinearProgress />}
						</Stack>
						<Button
							fullWidth
							type="submit"
							variant="contained"
							disabled={isSubmitting}
							sx={{ height: 50, mt: 4 }}
							onClick={(event) => {
								event.preventDefault();
								handleSubmit();
							}}
						>
							<FormattedMessage id="login.enter" defaultMessage="Log in" />
						</Button>
					</Form>
				)}
			</Formik>
			<Stack>
				<ForgotPasswordButton />
				<Divider
					sx={{ height: "2px", backgroundColor: theme.palette.disabled!.light }}
				/>
				<GoogleLogin setError={setGoogleError} />
				<Stack
					direction={{ mobile: "column", xs: "row" }}
					alignSelf="flex-end"
					alignItems={{ mobile: "flex-end", xs: "center" }}
					marginTop={1}
				>
					<RegisterButton />
				</Stack>
			</Stack>
		</Stack>
	);
};

export default Login;
